import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Menu, X, User } from 'lucide-react';
import Logo from './Logo';
import { useAuth } from '../contexts/AuthContext';
import { useAuthDialog } from '../contexts/AuthDialogContext';

const Navbar = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { openLogin } = useAuthDialog();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { to: '/courses', label: 'Courses', testId: 'navbar-courses-link' },
    { to: '/masterclasses', label: 'Masterclasses', testId: 'navbar-masterclasses-link' },
    { to: '/newsletter', label: 'Newsletter', testId: 'navbar-newsletter-link' },
    { to: '/about', label: 'About', testId: 'navbar-about-link' },
  ];

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed w-full z-50 transition-all duration-500 ${scrolled ? 'bg-black/90 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'}`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="navbar-logo-link" className="flex items-center gap-1 group">
            <Logo />
            <span className="brand-wordmark hidden sm:inline">CYNOPSIS</span>
          </Link>

          <div className="hidden lg:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={link.testId}
                className={`text-xs font-semibold tracking-[0.2em] uppercase transition-colors ${isActive(link.to) ? 'text-[#C8102E]' : 'text-zinc-300 hover:text-white'}`}
              >
                {link.label}
              </Link>
            ))}
            {!user && (
              <button
                type="button"
                onClick={openLogin}
                data-testid="navbar-login-link"
                className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-300 hover:text-white transition-colors"
              >
                Login
              </button>
            )}
            {user && (
              <Link
                to="/my-learning"
                data-testid="navbar-dashboard-link"
                className="flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-zinc-300 hover:text-white transition-colors"
              >
                <User className="w-4 h-4" />
                My Learning
              </Link>
            )}
            <Link
              to="/#waitlist"
              data-testid="navbar-waitlist-cta"
              className="btn-cine-primary btn-cine-sm rounded-md"
            >
              Join Waitlist
            </Link>
          </div>

          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-button" className="lg:hidden text-white p-2">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="lg:hidden bg-black border-t border-white/10" data-testid="mobile-menu">
          <div className="px-6 py-6 space-y-5">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileMenuOpen(false)}
                className="block text-xs font-semibold tracking-[0.2em] uppercase text-zinc-300 hover:text-white transition-colors"
              >
                {link.label}
              </Link>
            ))}
            {!user ? (
              <button
                type="button"
                onClick={() => {
                  openLogin();
                  setMobileMenuOpen(false);
                }}
                className="block w-full text-left text-xs font-semibold tracking-[0.2em] uppercase text-zinc-300 hover:text-white transition-colors"
              >
                Login
              </button>
            ) : (
              <Link
                to="/my-learning"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-xs font-semibold tracking-[0.2em] uppercase text-zinc-300 hover:text-white transition-colors"
              >
                My Learning
              </Link>
            )}
            <Link
              to="/#waitlist"
              onClick={() => setMobileMenuOpen(false)}
              className="btn-cine-primary btn-cine-sm rounded-md w-full justify-center"
            >
              Join Waitlist
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
